import { Box } from '@mui/material';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import {
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useConfirmationDialog = () => {
  type ConfirmationResult = {
    confirmed: boolean;
    closeDialog: () => void;
  };
  const [resolver, setResolver] = useState<
    ((result: ConfirmationResult) => void) | null
  >(null);
  const hasPending = !!resolver;

  /** Modal state */
  const [title, setTitle] = useState<string>('');
  const [bodyText, setBodyText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [autoConfirmKey, setAutoConfirmKey] = useState<string | undefined>(
    undefined,
  );
  const [confirmButtonColor, setConfirmButtonColor] = useState(
    ConfirmButtonColors.primary,
  );
  /** ************* */

  const { t } = useTranslation('workflowCompletions');

  const getUserConfirmation = (args: {
    title: string;
    bodyText: string;
    autoConfirmKey?: string;
    setConfirmButtonColor?: ConfirmButtonColors;
  }) =>
    new Promise<ConfirmationResult>((resolve) => {
      const serializedAutoConfirmKey =
        args.autoConfirmKey && `autoConfirm-${args.autoConfirmKey}`;
      if (
        serializedAutoConfirmKey &&
        localStorage.getItem(serializedAutoConfirmKey) === 'true'
      ) {
        resolve({
          confirmed: true,
          closeDialog,
        });
        return;
      }
      setAutoConfirmKey(serializedAutoConfirmKey);
      setTitle(args.title);
      setBodyText(args.bodyText);
      setResolver(() => resolve);
      setConfirmButtonColor(
        args.setConfirmButtonColor || ConfirmButtonColors.primary,
      );
    });

  const closeDialog = () => {
    setLoading(false);
    setAutoConfirmKey(undefined);
    setResolver(null);
  };

  const onConfirm = () => {
    setLoading(true);
    resolver?.({
      confirmed: true,
      closeDialog,
    });
  };

  const onCancel = () => {
    setLoading(true);
    resolver?.({
      confirmed: false,
      closeDialog,
    });
  };

  return {
    getUserConfirmation,
    confirmationDialog: (
      <ConfirmationModal
        confirmButtonColor={confirmButtonColor}
        open={hasPending}
        onConfirm={onConfirm}
        onCancel={onCancel}
        title={title}
        confirmButtonLabel={t('shared:confirm')}
        loading={loading}
      >
        <Typography variant='bodyLarge'>{bodyText}</Typography>
        {autoConfirmKey && (
          <Box sx={{ mt: 2 }}>
            <Checkbox
              size='medium'
              label={t('createAccountForGoal.confirmDialog.dontShowAgain')}
              checked={localStorage.getItem(autoConfirmKey) === 'true'}
              onChange={(checked) => {
                localStorage.setItem(
                  autoConfirmKey,
                  checked ? 'true' : 'false',
                );
              }}
            />
          </Box>
        )}
      </ConfirmationModal>
    ),
  };
};
